import Image from 'next/image'
import { ComponentPropsWithoutRef } from 'react'
import { AntennaIcon } from '~shared/icons/antenna'
import { cn } from '~shared/utils/cn'
import { replacePrepositionsWhitespaces } from '~shared/utils/replace-prepositions-whitespaces'

interface CircleProps {
  className?: string
}

const AntennaCircle = ({ className }: CircleProps) => {
  return (
    <div
      className={cn(
        'bg-accent rounded-full aspect-square w-[40px] md:w-[52px] 2xl:w-[54px] flex items-center justify-center text-primary',
        className
      )}>
      <AntennaIcon className='size-[18px] md:size-[24px]' />
    </div>
  )
}

const HeadphonesCircle = ({ className }: CircleProps) => {
  return (
    <Image
      src='/assets/imgs/main-page/ed-for-all-section-headphones.png'
      alt='Adornment headphones'
      width={900}
      height={900}
      className={cn('w-[300px] 2xl:w-[315px]', className)}
      draggable={false}
    />
  )
}

const GirlCircle = ({ className }: CircleProps) => {
  return (
    <Image
      src='/assets/imgs/main-page/ed-for-all-section-girl.png'
      alt='Adornment headphones'
      width={780}
      height={780}
      className={cn('w-[200px] md:w-[263px] 2xl:w-[276px]', className)}
      draggable={false}
    />
  )
}

const WifiCircle = ({ className }: CircleProps) => {
  return (
    <div
      className={cn(
        'aspect-square rounded-full flex items-center justify-center bg-secondary',
        'w-[175px] md:w-[225px] 2xl:w-[237px]',
        className
      )}>
      <Image
        src='/assets/imgs/main-page/ed-for-all-section-wifi.png'
        alt='Adornment headphones'
        width={450}
        height={450}
        className='w-full p-[24px]'
        draggable={false}
      />
    </div>
  )
}

export interface BannerProps extends ComponentPropsWithoutRef<'div'> {}

const Banner = (props: BannerProps) => {
  const { className, ...rest } = props
  return (
    <div
      className={cn(
        'select-none relative w-[355px] h-[255px] md:w-[704px] md:h-[342px] lg:w-[458px] lg:h-[342px] xl:w-[720px] xl:h-[342px] 2xl:w-[760px] 2xl:h-[360px]',
        className
      )}
      {...rest}>
      <HeadphonesCircle
        className={cn(
          'hidden md:block lg:hidden xl:block absolute left-0 top-[20px]'
          // 'inset-auto md:top-[0px] md:left-[0px]',
          // 'inset-auto lg:top-[0px] lg:left-[0px]',
          // 'inset-auto xl:top-[0px] xl:left-[0px]'
        )}
      />
      <GirlCircle
        className={cn(
          'absolute top-0 left-0',
          'md:left-auto md:right-[178px]',
          'lg:right-auto lg:left-0',
          'xl:left-auto xl:right-[195px]',
          '2xl:right-[205px]'
        )}
      />
      <WifiCircle
        className={cn(
          'absolute right-0 bottom-0'
          // 'md:inset-auto md:top-[0px] md:left-[0px]',
          // 'lg:inset-auto lg:top-[0px] lg:left-[0px]',
          // 'xl:inset-auto xl:top-[0px] xl:left-[0px]'
        )}
      />
      <AntennaCircle
        className={cn(
          'absolute bottom-0 right-[140px]',
          'md:left-auto md:right-[164px]',
          'lg:right-[181px]',
          '2xl:right-[190px]'
        )}
      />
      <p
        className={cn(
          'w-max flex items-center justify-center rounded-full leading-[1.2] bg-tertiary text-primary',
          'h-[28px] px-[12px] text-[12px]',
          'md:h-[37px] md:px-[16px] md:text-[16px]',
          'absolute bottom-[40px] left-0',
          'md:bottom-[38px] md:left-[230px]',
          'lg:bottom-[70px] lg:left-0',
          'xl:bottom-[38px] xl:left-[230px]',
          '2xl:bottom-[40px] 2xl:left-[240px]'
        )}>
        инновации
      </p>
      <p
        className={cn(
          'w-max flex items-center justify-center rounded-full leading-[1.2] bg-accent text-primary',
          'h-[28px] px-[12px] text-[12px]',
          'md:h-[37px] md:px-[16px] md:text-[16px]',
          'absolute top-[20px] right-[60px]',
          'md:top-[30px] md:right-[60px]',
          'lg:top-[30px] lg:right-[80px]'
        )}>
        онлайн&nbsp;обучение
      </p>
    </div>
  )
}

export interface EdForAllSectionProps
  extends ComponentPropsWithoutRef<'section'> {}

export const EdForAllSection = (props: EdForAllSectionProps) => {
  const { className = '', ...rest } = props

  return (
    <section
      className={cn(
        'container container-slim flex flex-col lg:flex-row gap-[30px] md:gap-[40px] lg:gap-[60px] xl:gap-[30px] 2xl:gap-[110px]',
        className
      )}
      {...rest}>
      <div className={cn('mt-[40px]')}>
        <h2 className='typo-section-heading'>
          Образование <br className='hidden xl:inline' />
          для&nbsp;всех
        </h2>
        <p className='typo-section-description mt-[16px] lg:mt-[24px]'>
          {replacePrepositionsWhitespaces(
            'Мы реализуем это через дистанционные образовательные программы, которые дают возможность учиться из любой точки мира. Инновационные технологии являются важной частью нашего учебного процесса, обеспечивая студентов всем необходимым для эффективного обучения.'
          )}
        </p>
      </div>
      <div className='flex justify-center shrink-0'>
        <Banner />
      </div>
    </section>
  )
}
